import { useState, useEffect, useRef } from "react";
import { PATTERN } from "../constants";

// シミュレーション関連の定数
const SIMULATION_CONSTANTS = {
  // 天候変更の間隔（ミリ秒）
  WEATHER_CHANGE_MIN: 8000,
  WEATHER_CHANGE_RANGE: 7000,

  // 速度と距離
  SPEED_KMH: 5,
  TOTAL_DISTANCE: 85,

  // バッテリー消費
  BASE_CONSUMPTION_PER_KM: 100 / 85, // 85kmで100%消費

  // フレームレート
  UPDATE_INTERVAL: 1000 / 60,
};

// 天候ごとの設定
const WEATHER_CONFIG = {
  sunny: {
    chargeRate: 0.8,
    consumptionMultiplier: 1.0,
    powerRatio: 1.0,
    tempo: 115,
    masterVolume: 0.4,
  },
  cloudy: {
    chargeRate: 0.1,
    consumptionMultiplier: 1.2,
    powerRatio: 0.4,
    tempo: 100,
    masterVolume: 0.6,
  },
  rainy: {
    chargeRate: 0.0,
    consumptionMultiplier: 1.5,
    powerRatio: 0.2,
    tempo: 80,
    masterVolume: 0.75,
  },
};

export const useSimulation = () => {
  // シミュレーションデータの状態
  const [simulationData, setSimulationData] = useState({
    weather: "sunny",
    battery: 100,
    distance_traveled: 0,
    distance_left: SIMULATION_CONSTANTS.TOTAL_DISTANCE,
    total_distance: SIMULATION_CONSTANTS.TOTAL_DISTANCE,
    time: 0,
    power_ratio: 1.0,
    tempo: 80,
    master_volume: 0.4,
    is_dissonant: false,
    is_paused: true,
    chord: "Cmaj7",
    step: 0,
    pattern: PATTERN,
    lastWeatherChangeTime: 0,
  });

  const [gameOver, setGameOver] = useState(null);

  // タイミング管理用のref
  const lastUpdateRef = useRef(Date.now());
  const weatherChangeIntervalRef = useRef(
    Math.random() * SIMULATION_CONSTANTS.WEATHER_CHANGE_RANGE +
      SIMULATION_CONSTANTS.WEATHER_CHANGE_MIN
  );
  const startTimeRef = useRef(Date.now());

  // ゲーム開始処理
  const startSimulation = () => {
    const now = Date.now();
    setSimulationData((prev) => ({
      ...prev,
      is_paused: false,
      lastWeatherChangeTime: now,
    }));
    startTimeRef.current = now;
    lastUpdateRef.current = now;
  };

  // 一時停止/再開の切り替え
  const togglePause = () => {
    setSimulationData((prev) => ({ ...prev, is_paused: !prev.is_paused }));
  };

  // ステップとコードの更新（音楽シーケンサーから呼ばれる）
  const updateMusicState = (step, chord) => {
    setSimulationData((prev) => ({
      ...prev,
      step,
      chord,
    }));
  };

  // バッテリーを100%にリフィル
  const stay = () => {
    setSimulationData((prev) => ({
      ...prev,
      battery: 100,
      is_paused: true,
    }));
  };

  // シミュレーション更新ループ
  useEffect(() => {
    if (gameOver) return;

    const intervalId = setInterval(() => {
      const now = Date.now();
      const deltaTime = (now - lastUpdateRef.current) / 1000;
      lastUpdateRef.current = now;

      setSimulationData((prev) => {
        const newData = { ...prev };

        // 経過時間の更新
        newData.time = (now - startTimeRef.current) / 1000;

        // 天候変更のチェック
        const timeSinceLastChange = now - prev.lastWeatherChangeTime;
        if (timeSinceLastChange > weatherChangeIntervalRef.current) {
          const weathers = ["sunny", "cloudy", "rainy"];
          const available = weathers.filter((w) => w !== prev.weather);
          newData.weather =
            available[Math.floor(Math.random() * available.length)];
          newData.lastWeatherChangeTime = now;
          weatherChangeIntervalRef.current =
            Math.random() * SIMULATION_CONSTANTS.WEATHER_CHANGE_RANGE +
            SIMULATION_CONSTANTS.WEATHER_CHANGE_MIN;
          console.log(`Weather changed to: ${newData.weather}`);
        }

        // バッテリー充電と消費の計算
        let newBattery = prev.battery;
        const weatherConfig = WEATHER_CONFIG[newData.weather];

        // 充電（天候による）
        newBattery = Math.min(
          100,
          newBattery + weatherConfig.chargeRate * deltaTime
        );

        // 走行による消費（一時停止中でなく、バッテリーがある場合）
        if (!prev.is_paused && prev.battery > 0) {
          const speedMps = (SIMULATION_CONSTANTS.SPEED_KMH * 1000) / 3600;
          const distanceThisFrame = speedMps * deltaTime;

          newData.distance_traveled =
            prev.distance_traveled + distanceThisFrame;
          newData.distance_left = Math.max(
            0,
            SIMULATION_CONSTANTS.TOTAL_DISTANCE - newData.distance_traveled
          );

          const consumption =
            distanceThisFrame *
            SIMULATION_CONSTANTS.BASE_CONSUMPTION_PER_KM *
            weatherConfig.consumptionMultiplier;

          newBattery = Math.max(0, newBattery - consumption);
        }

        newData.battery = newBattery;

        // 天候に基づく各種パラメータの更新
        newData.power_ratio = weatherConfig.powerRatio;
        newData.tempo = weatherConfig.tempo;
        newData.master_volume = weatherConfig.masterVolume;

        // バッテリー低下時の調整
        if (newData.battery < 50) {
          newData.tempo *= 0.8;
          newData.master_volume = Math.min(1.0, newData.master_volume * 1.2);
        }

        // 不協和音フラグ
        newData.is_dissonant = newData.battery < 25;

        // ゲームオーバー判定
        if (newData.distance_left <= 0) {
          setGameOver("goal");
        } else if (newData.battery <= 0 && !prev.is_paused) {
          setGameOver("battery");
        }

        return newData;
      });
    }, SIMULATION_CONSTANTS.UPDATE_INTERVAL);

    return () => clearInterval(intervalId);
  }, [gameOver]);

  return {
    simulationData,
    gameOver,
    startSimulation,
    togglePause,
    updateMusicState,
    stay,
  };
};
